import type { Priority } from "@/lib/api"
import { isWellFormedText } from "../../../convex/lib/feedbackPayload"

/**
 * The workspace board as the URL holds it: what narrows it. Pure, so a link
 * someone shares, a reload and the back button all give the same board.
 */
const PRIORITIES = ["none", "low", "medium", "high", "urgent"] as const satisfies readonly Priority[]
// Longer than any id or slug; only here to bound what the URL can carry.
const VALUE_MAX = 64
const VALUES_MAX = 20

export type BoardSearch = {
  /** A user id: only what is assigned to them. */
  assignee?: string
  priority?: Priority[]
  /** Project slugs. Left out, every project has its lane. */
  project?: string[]
}

const isValue = (value: unknown): value is string =>
  typeof value === "string" && value !== "" && value.length <= VALUE_MAX && isWellFormedText(value)

function listOf(raw: unknown): string[] {
  const values = Array.isArray(raw) ? raw : raw === undefined ? [] : [raw]
  return [...new Set(values.filter(isValue))].slice(0, VALUES_MAX)
}

/** Whatever the URL says, a board that can be drawn: it never throws. */
export function parseBoardSearch(raw: Record<string, unknown>): BoardSearch {
  const search: BoardSearch = {}
  if (isValue(raw.assignee)) search.assignee = raw.assignee
  const priority = listOf(raw.priority).filter((value): value is Priority =>
    (PRIORITIES as readonly string[]).includes(value),
  )
  if (priority.length > 0) search.priority = priority
  const project = listOf(raw.project)
  if (project.length > 0) search.project = project
  return search
}

/** What `issues.list` takes as `filters` for this search. */
export function boardFilters(search: BoardSearch): { assigneeId?: string[]; priority?: Priority[] } {
  return {
    ...(search.assignee ? { assigneeId: [search.assignee] } : {}),
    ...(search.priority ? { priority: search.priority } : {}),
  }
}
